<template>
  <div class="h-sd">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-sd__content">
      <!-- ===== Error ===== -->
      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load this subscription"
        :message="error"
        @retry="load"
      />

      <!-- ===== Loading ===== -->
      <HermesEmptyState v-else-if="loading" variant="loading" :skeleton-rows="6" title="Loading subscription" />

      <template v-else-if="agg">
        <!-- ===== Header ===== -->
        <div class="h-sd__head">
          <div>
            <div class="h-sd__id">
              <h1 class="h-sd__h1">Subscription #{{ sub.id }}</h1>
              <HermesStatusBadge :status="sub.effective_status" />
              <span v-if="sub.grant_type" class="h-sd__grant" :class="`h-sd__grant--${sub.grant_type}`">{{ grantLabel(sub.grant_type) }}</span>
              <span v-if="sub.is_single_use" class="h-sd__grant h-sd__grant--single">single-use</span>
              <span v-if="sub.cancel_at_period_end" class="h-sd__flag">cancels at period end</span>
            </div>
            <div class="h-sd__cust">
              <template v-if="endUser.name || endUser.email">
                <b v-if="endUser.name">{{ endUser.name }}</b>
                <span v-if="endUser.email"> · <b>{{ endUser.email }}</b></span>
              </template>
              <span v-if="product.name"> · {{ product.name }}</span>
              <span v-if="plan.name"> · {{ plan.name }}</span>
              <span v-if="plan.price_amount != null"> · {{ money(plan.price_amount, plan.price_currency) }}{{ intervalSuffix(plan.billing_interval) }}</span>
              <span v-if="sub.gateway_ref" class="h-mono"> · {{ sub.gateway_ref }}</span>
            </div>
          </div>
          <div class="h-sd__lifecycle">
            <button class="h-btn h-btn--secondary" :disabled="busy.extend" @click="extendModal.open = true">
              <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M12 8v4l3 2M12 3a9 9 0 1 0 9 9"/></svg>Extend
            </button>
            <button v-if="!sub.suspended" class="h-btn h-btn--secondary" :disabled="busy.suspend" @click="doSuspend">
              <svg viewBox="0 0 24 24" class="h-btn__i"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>Suspend
            </button>
            <button v-else class="h-btn h-btn--secondary" :disabled="busy.resume" @click="doResume">
              <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M5 3l14 9-14 9V3z"/></svg>Resume
            </button>
            <button class="h-btn h-btn--danger" :disabled="isCancelled" @click="openCancel">
              <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M18 6 6 18M6 6l12 12"/></svg>Cancel
            </button>
          </div>
        </div>

        <!-- ===== Two-column body ===== -->
        <div class="h-sd__cols">
          <!-- LEFT -->
          <div class="h-sd__stack">
            <!-- Status timeline -->
            <div class="h-card">
              <div class="h-card__head">
                <h3>Status timeline</h3>
                <span v-if="renewsLabel" class="h-badge h-badge--info"><span class="h-dot" style="background:var(--h-info)"></span>{{ renewsLabel }}</span>
              </div>
              <div v-if="!timeline.length" class="h-card__pad h-muted">No events recorded yet.</div>
              <div v-else class="h-tl">
                <div
                  v-for="(ev, i) in timeline"
                  :key="i"
                  class="h-tl__row"
                  :class="{ ok: !ev.scheduled, now: isNow(ev, i), last: i === timeline.length - 1 }"
                >
                  <div class="h-tl__node">
                    <svg v-if="!ev.scheduled" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    <span v-else class="h-tl__pip"></span>
                  </div>
                  <div class="h-tl__body">
                    <div class="h-tl__t" :class="{ 'h-tl__t--muted': ev.scheduled }">
                      {{ ev.title }}
                      <HermesStatusBadge v-if="ev.status" :status="ev.status" class="h-tl__badge" />
                    </div>
                    <div class="h-tl__d">{{ ev.detail }}</div>
                    <div class="h-tl__ts">{{ dateTime(ev.timestamp) }}<span v-if="ev.scheduled"> · scheduled</span></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment history -->
            <div class="h-card">
              <div class="h-card__head"><h3>Payment history</h3></div>
              <div v-if="!payments.length" class="h-card__pad h-muted">No payments captured for this customer yet.</div>
              <table v-else class="h-tbl">
                <thead>
                  <tr><th>Reference</th><th>Date</th><th>Gateway</th><th style="width:120px">Status</th><th class="h-r" style="width:110px">Amount</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in payments" :key="p.id">
                    <td class="h-tid">{{ p.gateway_ref || ('tx_' + p.id) }}</td>
                    <td>{{ date(p.paid_at || p.created) }}</td>
                    <td><span class="h-gw"><i :style="{ background: gatewayColor(p.gateway) }"></i>{{ gatewayLabel(p.gateway) }}</span></td>
                    <td><HermesStatusBadge :status="p.status" /></td>
                    <td class="h-r"><HermesMoneyCell :amount="p.amount" :currency="p.currency" /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Add-ons -->
            <div class="h-card">
              <div class="h-card__head"><h3>Add-ons attached</h3></div>
              <div v-if="!addons.recurring.length && !addons.one_off.length" class="h-card__pad h-muted">No add-ons attached.</div>
              <template v-else>
                <div v-for="a in addons.recurring" :key="'r' + a.id" class="h-addon">
                  <div class="h-addon__ic gold"><svg viewBox="0 0 24 24"><path d="M4 17l6-6-6-6M12 19h8"/></svg></div>
                  <div><div class="h-addon__t">{{ a.label }}</div><div class="h-addon__d">Recurring slot · meter {{ a.meter }}<span v-if="a.next_renewal_at"> · renews {{ date(a.next_renewal_at) }}</span></div></div>
                  <div class="h-addon__amt">
                    <div class="h-addon__a">{{ money(a.unit_price, a.currency) }}<span class="h-addon__int">/mo</span></div>
                    <HermesStatusBadge :status="a.status" class="h-addon__badge" />
                  </div>
                </div>
                <div v-for="a in addons.one_off" :key="'o' + a.id" class="h-addon">
                  <div class="h-addon__ic"><svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg></div>
                  <div><div class="h-addon__t">{{ a.label }}</div><div class="h-addon__d">One-off · meter {{ a.meter }}</div></div>
                  <div class="h-addon__amt">
                    <div class="h-addon__a">{{ money(a.total_price, a.currency) }}</div>
                    <span class="h-badge h-badge--neutral h-addon__badge"><span class="h-dot" style="background:var(--h-viz-neutral,#94A3B8)"></span>one-off</span>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- RIGHT rail -->
          <div class="h-sd__stack">
            <!-- Licence panel -->
            <div class="h-card" v-if="licence.kind === 'licenced'">
              <div class="h-card__head">
                <h3>Licence</h3>
                <HermesStatusBadge :status="jwtBadgeStatus" :label="jwtBadgeLabel" />
              </div>
              <div class="h-card__pad" style="padding-top:6px">
                <div class="h-lic">
                  <span class="h-lic__l">Heartbeat</span>
                  <span class="h-hb">
                    <span v-if="licence.heartbeat_fresh" class="h-pulse"></span>
                    <span v-else class="h-dot" :style="{ background: licence.last_seen_at ? 'var(--h-warning)' : 'var(--h-text-tertiary)' }"></span>
                    <b>{{ lastSeenLabel }}</b>
                  </span>
                </div>
                <div class="h-lic">
                  <span class="h-lic__l">Active tokens</span>
                  <span class="h-lic__v">{{ licence.active_tokens }}</span>
                </div>
                <div class="h-lic" v-if="licence.token_prefix" :style="licence.is_single_use ? '' : 'border-bottom:none'">
                  <span class="h-lic__l">Token prefix</span>
                  <span class="h-lic__v h-mono">{{ licence.token_prefix }}…</span>
                </div>
                <div class="h-lic" v-if="licence.is_single_use" style="border-bottom:none">
                  <span class="h-lic__l">Machine binding</span>
                  <span class="h-lic__v">
                    <template v-if="licence.bound_fingerprint">
                      <span class="h-bind h-bind--on">
                        <svg viewBox="0 0 24 24" class="h-bind__i"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        bound
                      </span>
                      <span class="h-mono h-bind__fp" :title="licence.bound_fingerprint">{{ shortFingerprint(licence.bound_fingerprint) }}</span>
                    </template>
                    <span v-else class="h-bind h-bind--off">
                      <svg viewBox="0 0 24 24" class="h-bind__i"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                      unbound
                    </span>
                  </span>
                </div>
                <p v-if="licence.is_single_use" class="h-lic__bindnote">
                  Single-use — bound to one install on its first heartbeat. Regenerating the activation token below
                  releases the binding so it can move to a new machine.
                </p>

                <div class="h-lic__tok">
                  <div class="h-lic__tokhead">
                    <span class="h-lic__toklbl">Activation token</span>
                    <button class="h-btn h-btn--secondary h-btn--sm" :disabled="busy.regen" @click="regenModal.open = true">
                      <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15"/></svg>
                      {{ busy.regen ? 'Regenerating…' : 'Regenerate' }}
                    </button>
                  </div>
                  <HermesApiKeyRevealField
                    v-if="newToken"
                    :value="newToken"
                    label=""
                    :start-revealed="true"
                    :shown-once="true"
                    warning="Shown once, just now. Regenerating again invalidates this token on the install's next heartbeat — copy it before you leave."
                  />
                  <p v-else class="h-lic__tokhint">The current token is stored hashed and never shown again. Regenerate to issue a fresh one (revokes the old on the install's next heartbeat).</p>
                </div>
              </div>
            </div>

            <!-- Tier enforcement -->
            <div class="h-card" v-if="enforcement">
              <div class="h-card__head">
                <h3>Tier enforcement</h3>
                <HermesStatusBadge :status="enforcementBadge.status" :label="enforcementBadge.label" />
              </div>
              <div class="h-card__pad">
                <div v-if="!enforcement.metrics.length" class="h-muted">No metered limits on this plan.</div>
                <div v-for="m in enforcement.metrics" :key="m.metric_key" class="h-meter">
                  <div class="h-meter__lbl">
                    <span>{{ prettyMetric(m.metric_key) }} <span class="h-meter__s">· {{ m.metric_key }}</span></span>
                    <HermesStatusBadge :status="meterStatus(m)" :label="meterLabel(m)" />
                  </div>
                  <div class="h-meter__bar"><i :style="{ width: meterWidth(m), background: meterColor(m) }"></i></div>
                  <div class="h-meter__val">{{ compact(m.used) }} / {{ m.limit === null ? '∞' : compact(m.limit) }}</div>
                </div>
              </div>
            </div>

            <!-- Details -->
            <div class="h-card">
              <div class="h-card__head"><h3>Details</h3></div>
              <div class="h-card__pad" style="padding-top:4px">
                <div class="h-kv"><span class="h-kv__k">End user</span><span class="h-kv__v">{{ endUser.name || endUser.email || '—' }}</span></div>
                <div class="h-kv"><span class="h-kv__k">Product · Plan</span><span class="h-kv__v">{{ (product.name || '—') }} · {{ (plan.name || '—') }}</span></div>
                <div class="h-kv"><span class="h-kv__k">Kind</span><span class="h-kv__v">{{ sub.kind }}</span></div>
                <div class="h-kv"><span class="h-kv__k">Gateway</span><span class="h-kv__v">{{ gatewayLabel(sub.gateway) }}</span></div>
                <div class="h-kv" v-if="sub.gateway_ref"><span class="h-kv__k">Gateway sub</span><span class="h-kv__v h-mono">{{ sub.gateway_ref }}</span></div>
                <div class="h-kv"><span class="h-kv__k">Created</span><span class="h-kv__v">{{ date(sub.created) }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ===== Extend modal ===== -->
    <teleport to="body">
      <div v-if="extendModal.open" class="h-scrim" @click.self="extendModal.open = false">
        <div class="h-modal" role="dialog" aria-label="Extend subscription">
          <div class="h-modal__head">
            <div class="h-modal__icon h-modal__icon--info"><svg viewBox="0 0 24 24"><path d="M12 8v4l3 2M12 3a9 9 0 1 0 9 9"/></svg></div>
            <div><h3 class="h-modal__title">Extend current period</h3><p class="h-modal__desc">Push the current period end forward. This changes Hermes only — the gateway's billing date is unchanged.</p></div>
            <button class="h-modal__x" aria-label="Close" @click="extendModal.open = false">✕</button>
          </div>
          <div class="h-modal__body">
            <label class="h-modal__lbl">Days to extend</label>
            <input class="h-modal__input" type="number" min="1" max="3650" v-model.number="extendModal.days" @keydown.enter="doExtend" />
          </div>
          <div class="h-modal__foot">
            <button class="h-btn h-btn--secondary" @click="extendModal.open = false">Cancel</button>
            <button class="h-btn h-btn--primary" :disabled="busy.extend || !(extendModal.days > 0)" @click="doExtend">Extend {{ extendModal.days > 0 ? extendModal.days + ' days' : '' }}</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ===== Regenerate-token modal ===== -->
    <teleport to="body">
      <div v-if="regenModal.open" class="h-scrim" @click.self="regenModal.open = false">
        <div class="h-modal" role="alertdialog" aria-label="Regenerate activation token">
          <div class="h-modal__head">
            <div class="h-modal__icon h-modal__icon--danger"><svg viewBox="0 0 24 24"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg></div>
            <div>
              <h3 class="h-modal__title">Regenerate activation token?</h3>
              <p class="h-modal__desc">
                The current token stops working on the install's next heartbeat, and that install loses its licence
                until it is given the new token.<template v-if="licence.is_single_use"> The machine binding is released.</template>
                The new token is shown once.
              </p>
            </div>
            <button class="h-modal__x" aria-label="Close" @click="regenModal.open = false">✕</button>
          </div>
          <div class="h-modal__foot">
            <button class="h-btn h-btn--secondary" @click="regenModal.open = false">Keep current token</button>
            <button class="h-btn h-btn--danger-solid" :disabled="busy.regen" @click="doRegenerate">Regenerate token</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ===== Cancel modal (typed CANCEL + mode radio) ===== -->
    <teleport to="body">
      <div v-if="cancelModal.open" class="h-scrim" @click.self="cancelModal.open = false">
        <div class="h-modal" role="dialog" aria-label="Cancel subscription">
          <div class="h-modal__head">
            <div class="h-modal__icon h-modal__icon--danger"><svg viewBox="0 0 24 24"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg></div>
            <div>
              <h3 class="h-modal__title">Cancel subscription #{{ sub.id }}</h3>
              <p class="h-modal__desc">Ends billing for <b>{{ endUser.email || endUser.name || 'this customer' }}</b>. The signed licence stops refreshing and lapses after its grace window.</p>
            </div>
            <button class="h-modal__x" aria-label="Close" @click="cancelModal.open = false">✕</button>
          </div>
          <div class="h-modal__body">
            <div class="h-radio">
              <label class="h-radio__opt" :class="{ on: cancelModal.mode === 'at_period_end' }">
                <input type="radio" value="at_period_end" v-model="cancelModal.mode" />
                <span><b>At period end</b><small>Stays active until {{ date(sub.current_period_end) || 'the period ends' }}.</small></span>
              </label>
              <label class="h-radio__opt" :class="{ on: cancelModal.mode === 'immediate' }">
                <input type="radio" value="immediate" v-model="cancelModal.mode" />
                <span><b>Immediately</b><small>Ends now. Only gateways with a native immediate-cancel accept this.</small></span>
              </label>
            </div>
            <div class="h-confirmbox">Type <span class="h-mono">CANCEL</span> to confirm.</div>
            <input class="h-modal__input" v-model="cancelModal.typed" placeholder="CANCEL" autocomplete="off" spellcheck="false" @keydown.enter="doCancel" />
          </div>
          <div class="h-modal__foot">
            <button class="h-btn h-btn--secondary" @click="cancelModal.open = false">Keep subscription</button>
            <button class="h-btn h-btn--danger-solid" :disabled="busy.cancel || cancelModal.typed !== 'CANCEL'" @click="doCancel">
              {{ cancelModal.mode === 'immediate' ? 'Cancel immediately' : 'Cancel at period end' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ===== Toasts ===== -->
    <div class="h-toasts" aria-live="polite">
      <div v-for="t in toasts" :key="t.id" class="h-toast" :class="`h-toast--${t.kind}`">{{ t.text }}</div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesSubscriptionDetail — Phase 3 subscription-detail island
// (admin-subscription-detail.html). Server-driven: HermesSubscriptionDetailPage
// mounts it by NAME with componentData.{apiBase, subscriptionId}. It fetches the
// full aggregate (GET /admin/subscriptions/{id}) and drives the lifecycle actions
// (suspend/resume/extend/cancel) + activation-token regenerate. Every mutation
// surfaces the gateway_synced caveat in its toast (Plan 007 risk #1). The
// timeline, payments, add-ons and enforcement meters render only real data.
export default {
  name: 'HermesSubscriptionDetail',
  props: {
    // The framework binds a Page component's setProperty() values as top-level
    // props (apiBase / subscriptionId / vendorName). `componentData` is kept as a
    // fallback for any caller that nests them.
    apiBase: { type: String, default: '/api/v1' },
    subscriptionId: { type: [Number, String], default: 0 },
    vendorName: { type: String, default: '' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      agg: null,
      loading: true,
      error: null,

      newToken: null, // plaintext, shown once after regenerate

      busy: { suspend: false, resume: false, extend: false, cancel: false, regen: false },
      extendModal: { open: false, days: 30 },
      cancelModal: { open: false, mode: 'at_period_end', typed: '' },
      regenModal: { open: false },

      toasts: [],
      toastSeq: 0,
    };
  },
  computed: {
    sub() { return this.agg?.subscription || {}; },
    endUser() { return this.agg?.end_user || {}; },
    plan() { return this.agg?.plan || {}; },
    product() { return this.agg?.product || {}; },
    timeline() { return this.agg?.timeline || []; },
    payments() { return this.agg?.payments || []; },
    addons() { return this.agg?.addons || { recurring: [], one_off: [] }; },
    licence() { return this.agg?.licence || { kind: '' }; },
    enforcement() { return this.agg?.enforcement || null; },
    crumbs() {
      return [{ label: 'Subscriptions', to: '/admin/subscriptions' }, { label: `#${this.subscriptionId}` }];
    },
    isCancelled() {
      return ['cancelled', 'expired'].includes(this.sub.status);
    },
    renewsLabel() {
      if (this.isCancelled || this.sub.cancel_at_period_end) return '';
      const end = this.sub.current_period_end;
      if (!end || end < Date.now() / 1000) return '';
      return `renews ${this.date(end)}`;
    },
    jwtBadgeStatus() {
      return { valid: 'valid', expiring: 'expiring', offline: 'offline', never_seen: 'offline' }[this.licence.jwt_status] || 'offline';
    },
    jwtBadgeLabel() {
      return { valid: 'valid', expiring: 'expiring', offline: 'offline', never_seen: 'never seen' }[this.licence.jwt_status] || 'unknown';
    },
    lastSeenLabel() {
      const s = this.licence.seconds_since_seen;
      if (s === null || s === undefined) return 'never seen';
      return this.relative(s) + ' ago';
    },
    enforcementBadge() {
      const s = this.enforcement?.status || 'ok';
      if (s === 'ok') return { status: 'ok', label: 'all within limit' };
      if (s === 'warning') return { status: 'warning', label: 'approaching' };
      return { status: s === 'suspend' ? 'suspended' : 'overage', label: s === 'suspend' ? 'over cap' : 'over limit' };
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        this.agg = await this.api.get(`${this.apiBase}/admin/subscriptions/${this.subscriptionId}`);
      } catch (e) {
        const status = e?.response?.status;
        this.error = status === 404
          ? 'This subscription does not exist or belongs to another vendor.'
          : `Failed to load subscription${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },

    // ── Lifecycle actions ──
    async doSuspend() { await this.action('suspend', 'suspend', `${this.apiBase}/admin/subscriptions/${this.subscriptionId}/suspend`); },
    async doResume() { await this.action('resume', 'resume', `${this.apiBase}/admin/subscriptions/${this.subscriptionId}/resume`); },
    async doExtend() {
      if (!(this.extendModal.days > 0)) return;
      const ok = await this.action('extend', 'extend', `${this.apiBase}/admin/subscriptions/${this.subscriptionId}/extend`, { days: this.extendModal.days });
      if (ok) this.extendModal.open = false;
    },
    openCancel() {
      this.cancelModal = { open: true, mode: 'at_period_end', typed: '' };
    },
    async doCancel() {
      if (this.cancelModal.typed !== 'CANCEL') return;
      const ok = await this.action('cancel', 'cancel', `${this.apiBase}/admin/subscriptions/${this.subscriptionId}/cancel`, {
        mode: this.cancelModal.mode,
        confirmation: this.cancelModal.typed,
      });
      if (ok) this.cancelModal.open = false;
    },

    async action(key, label, url, body) {
      this.busy[key] = true;
      try {
        const res = await this.api.post(url, body || {});
        const synced = res?.gateway_synced;
        const note = res?.note || '';
        const caveat = synced === false ? ` ${note}` : (note ? ` ${note}` : '');
        this.toast(`${this.cap(label)} applied${synced === false ? ' (Hermes-side only)' : synced === true ? ' (gateway synced)' : ''}.${caveat}`, 'success');
        await this.load();
        return true;
      } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data?.message || e?.message || 'Action failed';
        if (status === 422) this.toast(`Can't ${label}: ${msg}`, 'error');
        else if (status === 502) this.toast(`Gateway error — ${label} not applied. ${msg}`, 'error');
        else this.toast(`${this.cap(label)} failed: ${msg}`, 'error');
        return false;
      } finally {
        this.busy[key] = false;
      }
    },

    // ── Regenerate activation token ──
    async doRegenerate() {
      this.busy.regen = true;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/subscriptions/${this.subscriptionId}/activation-token/regenerate`, {});
        this.regenModal.open = false;
        this.newToken = res?.activation_token || null;
        const revoked = res?.revoked_previous || 0;
        this.toast(`New activation token issued${revoked ? ` · ${revoked} previous token(s) revoked` : ''}. Copy it now — shown once.`, 'success');
        await this.load();
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Regenerate failed';
        this.toast(`Couldn't regenerate token: ${msg}`, 'error');
      } finally {
        this.busy.regen = false;
      }
    },

    // ── Timeline helpers ──
    isNow(ev, i) {
      // The last non-scheduled event is the "current" node.
      if (ev.scheduled) return false;
      for (let j = i + 1; j < this.timeline.length; j++) {
        if (!this.timeline[j].scheduled) return false;
      }
      return true;
    },

    // ── Enforcement meters ──
    meterStatus(m) {
      if (m.limit === null) return 'metered';
      return { ok: 'ok', warning: 'warning', overage: 'overage', suspend: 'suspended' }[m.status] || 'ok';
    },
    meterLabel(m) {
      if (m.limit === null) return 'metered';
      return { ok: 'ok', warning: 'warning', overage: 'over limit', suspend: 'over cap' }[m.status] || 'ok';
    },
    meterColor(m) {
      if (m.limit === null) return 'var(--h-info)';
      return { ok: 'var(--h-success)', warning: 'var(--h-warning)', overage: 'var(--h-danger)', suspend: 'var(--h-danger)' }[m.status] || 'var(--h-success)';
    },
    meterWidth(m) {
      if (m.limit === null || m.limit === 0) return m.used > 0 ? '100%' : '4%';
      return `${Math.max(2, Math.min(100, Math.round((m.used / m.limit) * 100)))}%`;
    },
    prettyMetric(k) {
      return (k || '').replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },

    // ── Formatting ──
    money(v, currency) {
      if (v === null || v === undefined || v === '') return '—';
      const sym = { GBP: '£', USD: '$', EUR: '€' }[currency] || '';
      const n = Number(v);
      return `${sym}${Number.isNaN(n) ? v : n.toFixed(2)}`;
    },
    intervalSuffix(interval) {
      return { monthly: '/mo', quarterly: '/qtr', yearly: '/yr', one_time: '' }[interval] || '';
    },
    date(v) {
      if (!v) return '';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    dateTime(v) {
      if (!v) return '';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    relative(seconds) {
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
      return `${Math.floor(seconds / 86400)}d`;
    },
    compact(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return v;
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
      return `${n % 1 === 0 ? n : n.toFixed(1)}`;
    },
    gatewayLabel(g) {
      return { stripe: 'Stripe', revolut: 'Revolut', create_pay: 'Create Pay', createpay: 'Create Pay', none: '—', gateway: 'Gateway' }[g] || (g ? this.cap(g) : '—');
    },
    gatewayColor(g) {
      return { stripe: '#635BFF', revolut: '#191C1F', create_pay: '#0EA5E9' }[g] || '#94A3B8';
    },
    cap(s) { return (s || '').charAt(0).toUpperCase() + (s || '').slice(1); },
    grantLabel(g) { return { trial: 'Trial', comp: 'Comp', lifetime: 'Lifetime' }[g] || g; },
    shortFingerprint(fp) {
      if (!fp) return '';
      return fp.length > 16 ? `${fp.slice(0, 12)}…` : fp;
    },

    toast(text, kind = 'success') {
      const id = ++this.toastSeq;
      this.toasts.push({ id, text, kind });
      setTimeout(() => { this.toasts = this.toasts.filter((t) => t.id !== id); }, 5200);
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-sd { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-sd__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-sd__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-sd__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-mono { font-family: var(--h-font-mono, monospace); }
.h-muted { color: var(--h-text-tertiary, #94A3B8); font-size: 13px; }

/* header */
.h-sd__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-sd__id { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.h-sd__h1 { font-size: 23px; font-weight: 600; letter-spacing: -0.02em; margin: 0; }
.h-sd__flag { font-size: 11.5px; font-weight: 600; color: var(--h-warning-text, #B45309); background: var(--h-warning-bg, #FFFBEB); border: 1px solid var(--h-warning-border, #FDE68A); border-radius: 999px; padding: 3px 9px; }
.h-sd__grant { font-size: 11.5px; font-weight: 600; border-radius: 999px; padding: 3px 10px; border: 1px solid transparent; }
.h-sd__grant--trial { color: var(--h-violet-text, #6D28D9); background: var(--h-violet-bg, #F5F3FF); border-color: var(--h-violet-border, #DDD6FE); }
.h-sd__grant--lifetime { color: var(--h-apexl-gold-dark, #8B7355); background: var(--h-apexl-gold-light, #F6F1E7); border-color: var(--h-apexl-gold, #C9A227); }
.h-sd__grant--comp { color: var(--h-neutral-text, #475569); background: var(--h-neutral-bg, #F1F5F9); border-color: var(--h-neutral-border, #E2E8F0); }
.h-sd__grant--single { color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); border-color: var(--h-border, #E2E8F0); display: inline-flex; align-items: center; }
.h-sd__cust { font-size: 13.5px; color: var(--h-text-secondary, #64748B); margin-top: 6px; }
.h-sd__cust .h-mono { color: var(--h-text-tertiary, #94A3B8); }
.h-sd__lifecycle { display: flex; gap: 10px; align-items: center; flex: none; }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; line-height: 1; white-space: nowrap; color: var(--h-text-primary); }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.h-btn__i { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary); border-color: var(--h-border-strong, #CBD5E1); }
.h-btn--secondary:hover:not(:disabled) { background: var(--h-bg-subtle, #F1F5F9); }
.h-btn--sm { font-size: 12.5px; padding: 6px 11px; }
.h-btn--danger { background: var(--h-bg-card, #fff); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); }
.h-btn--danger:hover:not(:disabled) { background: var(--h-danger-bg, #FEF2F2); }
.h-btn--danger-solid { background: var(--h-danger, #EF4444); color: #fff; border-color: transparent; }
.h-btn--danger-solid:hover:not(:disabled) { background: var(--h-danger-hover, #DC2626); }

/* layout */
.h-sd__cols { display: grid; grid-template-columns: 1fr 400px; gap: 20px; align-items: start; }
.h-sd__stack { display: flex; flex-direction: column; gap: 20px; min-width: 0; }
.h-card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
.h-card__head { display: flex; align-items: center; justify-content: space-between; padding: 15px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-card__head h3 { margin: 0; font-size: 15px; font-weight: 600; }
.h-card__pad { padding: 18px; }

.h-badge { display: inline-flex; align-items: center; gap: 6px; padding: 3px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 600; border: 1px solid transparent; white-space: nowrap; }
.h-badge--info { background: var(--h-info-bg); color: var(--h-info-text); border-color: var(--h-info-border); }
.h-badge--neutral { background: var(--h-neutral-bg); color: var(--h-neutral-text); border-color: var(--h-neutral-border); }
.h-dot { width: 6px; height: 6px; border-radius: 50%; }

/* timeline */
.h-tl { padding: 8px 20px 6px; }
.h-tl__row { display: flex; gap: 14px; padding: 12px 0; position: relative; }
.h-tl__row:not(.last)::after { content: ''; position: absolute; left: 11px; top: 30px; bottom: -12px; width: 2px; background: var(--h-border, #E2E8F0); }
.h-tl__row.ok:not(.last)::after { background: var(--h-accent, #0EA5E9); }
.h-tl__node { width: 24px; height: 24px; border-radius: 50%; flex: none; display: flex; align-items: center; justify-content: center; z-index: 1; border: 2px solid var(--h-border-strong, #CBD5E1); background: var(--h-bg-card, #fff); }
.h-tl__row.ok .h-tl__node { background: var(--h-accent, #0EA5E9); border-color: var(--h-accent, #0EA5E9); }
.h-tl__row.ok .h-tl__node svg { width: 12px; height: 12px; stroke: #fff; fill: none; stroke-width: 3; }
.h-tl__row.now .h-tl__node { box-shadow: var(--h-focus-ring); }
.h-tl__pip { width: 7px; height: 7px; border-radius: 50%; background: var(--h-text-tertiary, #94A3B8); }
.h-tl__t { font-size: 13.5px; font-weight: 600; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.h-tl__t--muted { color: var(--h-text-tertiary, #94A3B8); }
.h-tl__d { font-size: 12.5px; color: var(--h-text-secondary, #64748B); margin-top: 1px; }
.h-tl__ts { font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); font-family: var(--h-font-mono, monospace); margin-top: 3px; }

/* payments table */
.h-tbl { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.h-tbl thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); padding: 10px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-tbl thead th.h-r { text-align: right; }
.h-tbl tbody td { padding: 11px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); vertical-align: middle; }
.h-tbl tbody tr:last-child td { border-bottom: none; }
.h-tbl td.h-r { text-align: right; }
.h-tid { font-family: var(--h-font-mono, monospace); font-size: 12px; color: var(--h-text-tertiary, #94A3B8); }
.h-gw { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--h-text-secondary, #64748B); }
.h-gw i { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }

/* addons */
.h-addon { display: flex; align-items: center; gap: 13px; padding: 13px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-addon:last-child { border-bottom: none; }
.h-addon__ic { width: 34px; height: 34px; border-radius: 8px; background: var(--h-accent-light, #E0F2FE); color: var(--h-accent-dark, #0369A1); display: flex; align-items: center; justify-content: center; flex: none; }
.h-addon__ic svg { width: 17px; height: 17px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-addon__ic.gold { background: var(--h-apexl-gold-light, #F6F1E7); color: var(--h-apexl-gold-dark, #8B7355); }
.h-addon__t { font-size: 13.5px; font-weight: 600; }
.h-addon__d { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); }
.h-addon__amt { margin-left: auto; text-align: right; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.h-addon__a { font-family: var(--h-font-mono, monospace); font-weight: 600; font-variant-numeric: tabular-nums; }
.h-addon__int { font-weight: 400; color: var(--h-text-tertiary, #94A3B8); }
.h-addon__badge { margin-top: 0; }

/* licence panel */
.h-lic { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid var(--h-border, #E2E8F0); font-size: 13px; }
.h-lic__l { color: var(--h-text-secondary, #64748B); }
.h-lic__v { font-weight: 500; }
.h-hb { display: flex; align-items: center; gap: 8px; font-size: 12.5px; }
.h-pulse { width: 8px; height: 8px; border-radius: 50%; background: var(--h-success, #10B981); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); animation: h-pl 1.8s infinite; }
@keyframes h-pl { 0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); } 70% { box-shadow: 0 0 0 7px rgba(16, 185, 129, 0); } 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
.h-lic__tok { margin-top: 12px; padding-top: 14px; border-top: 1px solid var(--h-border, #E2E8F0); }
.h-lic__tokhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.h-lic__toklbl { font-size: 13px; font-weight: 500; }
.h-lic__tokhint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 8px 0 0; line-height: 1.5; }
.h-lic__bindnote { font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); margin: 8px 0 0; line-height: 1.5; }
.h-bind { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; padding: 2px 8px; border-radius: 999px; border: 1px solid transparent; }
.h-bind--on { color: var(--h-success-text, #047857); background: var(--h-success-bg, #ECFDF5); border-color: var(--h-success-border, #A7F3D0); }
.h-bind--off { color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); border-color: var(--h-border, #E2E8F0); }
.h-bind__i { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-bind__fp { margin-left: 8px; font-size: 11px; color: var(--h-text-tertiary, #94A3B8); }

/* meters */
.h-meter { padding: 11px 0; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-meter:last-child { border-bottom: none; }
.h-meter__lbl { font-size: 13px; margin-bottom: 7px; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.h-meter__s { color: var(--h-text-tertiary, #94A3B8); font-size: 11.5px; }
.h-meter__bar { height: 8px; background: var(--h-neutral-bg, #F1F5F9); border-radius: 999px; overflow: hidden; }
.h-meter__bar i { display: block; height: 100%; border-radius: 999px; }
.h-meter__val { text-align: right; font-family: var(--h-font-mono, monospace); font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); margin-top: 5px; }

/* KV */
.h-kv { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid var(--h-border, #E2E8F0); font-size: 13px; gap: 12px; }
.h-kv:last-child { border-bottom: none; }
.h-kv__k { color: var(--h-text-secondary, #64748B); }
.h-kv__v { font-weight: 500; text-align: right; }

/* modals */
.h-scrim { position: fixed; inset: 0; background: var(--h-bg-overlay, rgba(15,23,42,.55)); display: flex; align-items: center; justify-content: center; z-index: 1300; padding: 24px; }
.h-modal { width: 100%; max-width: 460px; background: var(--h-bg-card, #fff); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-pop); overflow: hidden; border: 1px solid var(--h-border, #E2E8F0); }
.h-modal__head { padding: 20px 22px 0; display: flex; gap: 14px; align-items: flex-start; }
.h-modal__icon { flex: none; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.h-modal__icon svg { width: 20px; height: 20px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-modal__icon--danger { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); }
.h-modal__icon--info { background: var(--h-info-bg, #E0F2FE); color: var(--h-info-text, #0369A1); }
.h-modal__title { font-size: 16px; font-weight: 600; margin: 0 0 4px; }
.h-modal__desc { font-size: 13.5px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-modal__x { margin-left: auto; border: none; background: none; color: var(--h-text-tertiary, #94A3B8); cursor: pointer; font-size: 18px; line-height: 1; padding: 2px; }
.h-modal__body { padding: 14px 22px 4px; }
.h-modal__lbl { font-size: 13px; font-weight: 500; display: block; margin-bottom: 6px; }
.h-modal__input { width: 100%; margin-top: 4px; font: inherit; font-size: 14px; padding: 9px 12px; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: 6px; background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); box-sizing: border-box; }
.h-modal__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-modal__foot { display: flex; justify-content: flex-end; gap: 10px; padding: 18px 22px 20px; }
.h-confirmbox { background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); padding: 11px 13px; font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 12px; }
.h-confirmbox .h-mono { background: rgba(0,0,0,.06); padding: 1px 5px; border-radius: 4px; }
.h-radio { display: flex; flex-direction: column; gap: 8px; }
.h-radio__opt { display: flex; align-items: flex-start; gap: 10px; padding: 11px 13px; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); cursor: pointer; font-size: 13px; }
.h-radio__opt.on { border-color: var(--h-accent, #0EA5E9); background: var(--h-accent-light, #E0F2FE); }
.h-radio__opt input { margin-top: 2px; }
.h-radio__opt b { display: block; font-size: 13px; }
.h-radio__opt small { display: block; color: var(--h-text-secondary, #64748B); font-size: 12px; margin-top: 2px; }

/* toasts */
.h-toasts { position: fixed; bottom: 24px; right: 24px; z-index: 1400; display: flex; flex-direction: column; gap: 10px; max-width: 420px; }
.h-toast { padding: 11px 16px; border-radius: var(--h-radius, 6px); font-size: 13px; font-weight: 500; box-shadow: var(--h-shadow-panel); border: 1px solid transparent; }
.h-toast--success { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); border-color: var(--h-success-border, #A7F3D0); }
.h-toast--error { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); }

@media (max-width: 1080px) {
  .h-sd__cols { grid-template-columns: 1fr; }
}
</style>
